import { createFileRoute } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, MapPin, MoreHorizontal, Pencil, Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { listLocations, upsertLocation, deleteLocation } from "@/lib/weather.functions";

export const Route = createFileRoute("/app/locations")({
  head: () => ({
    meta: buildAppMeta({
      title: "Locations — CoachQuick",
      description:
        "Manage your coaching locations, courts, and default weather forecasts for each venue.",
      path: "/app/locations",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/locations` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <AppShell>
        <LocationsPage />
      </AppShell>
    </RequireAuth>
  ),
});

async function authHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : undefined;
}

type LocationRow = Awaited<ReturnType<typeof listLocations>>[number];

const EMPTY = {
  id: undefined as string | undefined,
  name: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  country: "US",
  indoor: false,
  sport: "pickleball" as "pickleball" | "tennis" | "golf" | "generic",
  is_default: false,
  notes: "",
};

/*
 * Location card header is a purely abstract gradient band:
 *   outdoor -> fresh green (primary gradient)
 *   indoor  -> dark slate (muted/card gradient)
 * No sport-specific imagery so the same card works for pickleball, golf, tennis, etc.
 */

function LocationsPage() {
  const fetchList = useServerFn(listLocations);
  const upsert = useServerFn(upsertLocation);
  const del = useServerFn(deleteLocation);
  const qc = useQueryClient();
  const [editing, setEditing] = useState<typeof EMPTY | null>(null);

  const q = useQuery({
    queryKey: ["coach-locations"],
    queryFn: async () => {
      const headers = await authHeaders();
      return (await fetchList({ headers })) as LocationRow[];
    },
  });

  const saveMut = useMutation({
    mutationFn: async (vals: typeof EMPTY) => {
      const headers = await authHeaders();
      return await upsert({ data: vals, headers });
    },
    onSuccess: () => {
      toast.success("Location saved");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["coach-locations"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => {
      const headers = await authHeaders();
      return await del({ data: { id }, headers });
    },
    onSuccess: () => {
      toast.success("Location removed");
      qc.invalidateQueries({ queryKey: ["coach-locations"] });
    },
  });

  const rows = q.data ?? [];
  const outdoorCount = rows.filter((r) => !r.indoor).length;

  return (
    <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {rows.length} location{rows.length === 1 ? "" : "s"} · {outdoorCount} pulling weather
          </p>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight text-foreground md:text-5xl">
            Locations
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Where you teach. Used to fetch weather forecasts for upcoming outdoor lessons and to
            fill the location field when logging a session.
          </p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-[10px]">
          <Plus className="h-4 w-4" /> Add location
        </Button>
      </div>

      {q.isLoading ? (
        <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((r) => (
            <LocationCard
              key={r.id}
              row={r}
              onEdit={() =>
                setEditing({
                  id: r.id,
                  name: r.name,
                  address: r.address ?? "",
                  city: r.city ?? "",
                  state: r.state ?? "",
                  zip: r.zip,
                  country: r.country,
                  indoor: r.indoor,
                  sport: r.sport,
                  is_default: r.is_default,
                  notes: r.notes ?? "",
                })
              }
              onSetDefault={() =>
                saveMut.mutate({
                  id: r.id,
                  name: r.name,
                  address: r.address ?? "",
                  city: r.city ?? "",
                  state: r.state ?? "",
                  zip: r.zip,
                  country: r.country,
                  indoor: r.indoor,
                  sport: r.sport,
                  is_default: true,
                  notes: r.notes ?? "",
                })
              }
              onDelete={() => {
                if (confirm(`Delete "${r.name}"?`)) delMut.mutate(r.id);
              }}
            />
          ))}

          {/* "Add a location" trailing tile */}
          <button
            type="button"
            onClick={() => setEditing({ ...EMPTY })}
            className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-[16px] border border-dashed border-border bg-transparent p-6 text-center transition hover:border-foreground/30 hover:bg-card"
          >
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#9fe870]/30">
              <Plus className="h-5 w-5 text-[#054d28]" />
            </span>
            <span className="text-sm font-bold text-foreground">Add a location</span>
            <span className="max-w-[14rem] text-xs text-muted-foreground">
              We'll pull weather automatically for outdoor spots.
            </span>
          </button>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:rounded-[16px]">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit location" : "Add location"}</DialogTitle>
            <DialogDescription>
              Where you teach. The zip is used to fetch weather.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="loc-name">Name</Label>
                <Input
                  id="loc-name"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Central Park Courts"
                  className="mt-1.5"
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label htmlFor="loc-addr">Street (optional)</Label>
                  <Input
                    id="loc-addr"
                    value={editing.address}
                    onChange={(e) => setEditing({ ...editing, address: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="loc-zip">Zip</Label>
                  <Input
                    id="loc-zip"
                    value={editing.zip}
                    onChange={(e) => setEditing({ ...editing, zip: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <Label htmlFor="loc-city">City</Label>
                  <Input
                    id="loc-city"
                    value={editing.city}
                    onChange={(e) => setEditing({ ...editing, city: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="loc-state">State</Label>
                  <Input
                    id="loc-state"
                    value={editing.state}
                    onChange={(e) => setEditing({ ...editing, state: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Sport</Label>
                  <Select
                    value={editing.sport}
                    onValueChange={(v) =>
                      setEditing({ ...editing, sport: v as typeof editing.sport })
                    }
                  >
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pickleball">Pickleball</SelectItem>
                      <SelectItem value="tennis">Tennis</SelectItem>
                      <SelectItem value="golf">Golf</SelectItem>
                      <SelectItem value="generic">Other / generic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Country</Label>
                  <Input
                    value={editing.country}
                    onChange={(e) =>
                      setEditing({ ...editing, country: e.target.value.toUpperCase() })
                    }
                    maxLength={3}
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-[10px] border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Indoor venue</p>
                  <p className="text-xs text-muted-foreground">
                    Indoor sessions only flag on severe weather alerts.
                  </p>
                </div>
                <Switch
                  checked={editing.indoor}
                  onCheckedChange={(v) => setEditing({ ...editing, indoor: v })}
                />
              </div>
              <div className="flex items-center justify-between rounded-[10px] border border-border p-3">
                <div>
                  <p className="text-sm font-medium">Default location</p>
                  <p className="text-xs text-muted-foreground">
                    Used when assigning a location to new lessons.
                  </p>
                </div>
                <Switch
                  checked={editing.is_default}
                  onCheckedChange={(v) => setEditing({ ...editing, is_default: v })}
                />
              </div>
              <div>
                <Label htmlFor="loc-notes">Notes (optional)</Label>
                <Textarea
                  id="loc-notes"
                  value={editing.notes}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  rows={2}
                  className="mt-1.5"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => editing && saveMut.mutate(editing)}
              disabled={!editing?.name || !editing?.zip || saveMut.isPending}
            >
              {saveMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LocationCard({
  row,
  onEdit,
  onSetDefault,
  onDelete,
}: {
  row: LocationRow;
  onEdit: () => void;
  onSetDefault: () => void;
  onDelete: () => void;
}) {
  const addressLine = [row.address, row.city, row.state].filter(Boolean).join(", ");
  const sportLabel = (row.sport ?? "pickleball").toString();
  const isOutdoor = !row.indoor;

  return (
    <div className="overflow-hidden rounded-[16px] border bg-card">
      {/* Abstract gradient header — green for outdoor, dark slate for indoor */}
      <div
        className={`relative h-[92px] ${
          isOutdoor
            ? "bg-gradient-to-br from-primary to-primary-glow"
            : "bg-gradient-to-br from-[#0e0f0c] to-[#1a1c19]"
        }`}
      >
        <div className="absolute right-3 top-3 flex items-center gap-1.5">
          <button
            type="button"
            onClick={onEdit}
            aria-label="Edit"
            className={`flex h-7 w-7 items-center justify-center rounded-[8px] shadow-sm backdrop-blur transition hover:scale-105 ${
              isOutdoor
                ? "bg-[#0e0f0c]/80 text-white hover:bg-[#0e0f0c]"
                : "bg-white/80 text-[#0e0f0c] hover:bg-white"
            }`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Location actions"
                className={`flex h-7 w-7 items-center justify-center rounded-[8px] shadow-sm backdrop-blur transition hover:scale-105 ${
                  isOutdoor
                    ? "bg-[#0e0f0c]/80 text-white hover:bg-[#0e0f0c]"
                    : "bg-white/80 text-[#0e0f0c] hover:bg-white"
                }`}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={onEdit}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </DropdownMenuItem>
              {!row.is_default && (
                <DropdownMenuItem onSelect={onSetDefault}>
                  <Star className="h-3.5 w-3.5" /> Set as default
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={onDelete}
                className="text-destructive focus:text-destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Body */}
      <div className="p-[18px]">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-[17px] font-bold leading-tight">{row.name}</h3>
          {row.is_default && (
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#0e0f0c] px-2 py-0.5 text-[10px] font-semibold text-white">
              <MapPin className="h-3 w-3" /> Default
            </span>
          )}
        </div>
        <p className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3" />
          {addressLine || row.zip}
        </p>

        <div className="mt-3 flex flex-wrap gap-1.5">
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold capitalize text-muted-foreground">
            {row.indoor ? "Indoor" : "Outdoor"}
          </span>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">
            {sportLabel}
          </span>
          {row.zip && (
            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              {row.zip}
            </span>
          )}
        </div>

        {!row.indoor && (
          <div className="mt-4 flex items-center justify-between rounded-[10px] bg-[#e8ebe6] px-3 py-2.5 dark:bg-white/5">
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-white/70 text-muted-foreground dark:bg-white/10">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M7 16a4 4 0 010-8 5 5 0 0110 0 4 4 0 010 8H7z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="leading-tight">
                <p className="font-display text-base font-black text-foreground">—</p>
                <p className="text-[11px] text-muted-foreground">
                  Forecast updates near lesson time
                </p>
              </div>
            </div>
            <span className="text-[11px] font-semibold text-muted-foreground">10-day →</span>
          </div>
        )}

        {row.latitude === null && (
          <p className="mt-3 text-[11px] text-[#b86700] dark:text-[#fcd34d]">
            Could not geocode this zip — weather will be unavailable until fixed.
          </p>
        )}

        <div className="mt-4 grid grid-cols-3 gap-2 border-t pt-3">
          <Stat label="This month" value="—" big />
          <Stat label="Clients" value="—" big />
          <Stat label="Last used" value="—" />
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, big }: { label: string; value: string; big?: boolean }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      {big ? (
        <p className="mt-0.5 font-display text-xl font-black leading-none tracking-tight">
          {value}
        </p>
      ) : (
        <p className="mt-0.5 text-sm font-semibold">{value}</p>
      )}
    </div>
  );
}
